import React, { useState, useEffect, useCallback } from "react";
import { api } from "../utils/api";
import { useI18n } from "../contexts/I18nContext";
import PhotoUpload from "./PhotoUpload";
import { DEPARTMENTS, REGIONS } from "../data/departments";

const CATEGORIES = [
  { value: "", label: "Tous", icon: "🔍" },
  { value: "club", label: "Club libertin", icon: "🌙" },
  { value: "sauna", label: "Sauna / Spa", icon: "♨️" },
  { value: "bar", label: "Bar", icon: "🍸" },
  { value: "hotel", label: "Hôtel", icon: "🏨" },
  { value: "plage", label: "Plage / Nature", icon: "🏖" },
  { value: "camping", label: "Camping", icon: "⛺" },
  { value: "restaurant", label: "Restaurant", icon: "🍽" },
  { value: "association", label: "Association", icon: "🏠" },
  { value: "boutique", label: "Boutique", icon: "🛍" },
  { value: "online", label: "En ligne", icon: "💻" },
  { value: "other", label: "Autre", icon: "✦" },
];

const FEATURE_OPTIONS = [
  "Couples", "Solo hommes", "Solo femmes", "LGBTQ+ friendly",
  "Jacuzzi", "Darkroom", "Fumoir", "Terrasse", "Parking privé",
  "Vestiaires", "Serviettes fournies", "Bar", "Restaurant",
  "PMR", "Discret", "Dress code", "Soirées à thème",
];

const PRICE_OPTIONS = [
  { value: "", label: "Non précisé" },
  { value: "gratuit", label: "Gratuit" },
  { value: "€", label: "€ (< 30€)" },
  { value: "€€", label: "€€ (30-80€)" },
  { value: "€€€", label: "€€€ (> 80€)" },
];

function Stars({ rating, interactive, onChange }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type={interactive ? "button" : undefined}
          onClick={() => interactive && onChange?.(n)}
          className={`text-lg ${interactive ? "cursor-pointer" : "cursor-default"} ${n <= rating ? "text-amber-400" : "text-sensoria-border"}`}
          disabled={!interactive}>★</button>
      ))}
    </div>
  );
}

export default function Places() {
  const { t } = useI18n();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCity, setFilterCity] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [reviewingPlace, setReviewingPlace] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    name: "", description: "", category: "club", city: "",
    department: "", region: "",
    address: "", website: "", price_range: "", features: [],
  });
  const [review, setReview] = useState({ rating: 0, ambiance_rating: 0, respect_rating: 0, hygiene_rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filterRegion, setFilterRegion] = useState("");

  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchCity) params.set("city", searchCity);
      if (filterCat) params.set("category", filterCat);
      const data = await api(`/api/places?${params}`);
      setPlaces(data.places || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [searchCity, filterCat]);

  useEffect(() => { fetchPlaces(); }, [fetchPlaces]);

  function handleCitySearch(e) { e.preventDefault(); setSearchCity(filterCity); }

  function toggleFeature(f) {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(f) ? prev.features.filter((x) => x !== f) : [...prev.features, f],
    }));
  }

  async function handleSubmitPlace(e) {
    e.preventDefault(); setSubmitting(true); setError("");
    try {
      const body = { ...form, content: `${form.name} ${form.description}` };
      const data = await api("/api/places", { method: "POST", body: JSON.stringify(body) });
      setPlaces((prev) => [data.place, ...prev]);
      setShowForm(false);
      setForm({ name: "", description: "", category: "club", city: "", address: "", website: "", price_range: "", features: [] });
      setSuccess("Lieu soumis !"); setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.data?.detail || err.message); }
    finally { setSubmitting(false); }
  }

  async function handleSubmitReview(placeId) {
    if (review.rating === 0) { setError("Donnez une note."); return; }
    setSubmitting(true); setError("");
    try {
      const body = { ...review, content: review.comment };
      await api(`/api/places/${placeId}/reviews`, { method: "POST", body: JSON.stringify(body) });
      setReviewingPlace(null);
      setReview({ rating: 0, ambiance_rating: 0, respect_rating: 0, hygiene_rating: 0, comment: "" });
      setSuccess("Avis publié !"); setTimeout(() => setSuccess(""), 3000);
      fetchPlaces();
    } catch (err) { setError(err.data?.detail || err.message); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold text-white">{t("places_title")}</h2>
          <p className="mt-1 text-sm text-sensoria-text-dim">{t("places_desc")}</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="sensoria-btn flex-shrink-0 px-4 py-2 text-sm">
          {showForm ? t("cancel") : t("places_add")}
        </button>
      </div>

      {success && <div className="rounded-lg border border-sensoria-success/30 bg-sensoria-success/10 px-4 py-3 text-sm text-sensoria-success">{success}</div>}
      {error && <div className="rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-4 py-3 text-sm text-sensoria-danger">{error} <button onClick={() => setError("")} className="ml-2 underline">fermer</button></div>}

      {showForm && (
        <form onSubmit={handleSubmitPlace} className="sensoria-card sensoria-glow space-y-4 animate-slide-up">
          <h3 className="font-display text-lg font-semibold text-white">{t("places_suggest")}</h3>
          <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Nom du lieu" maxLength={150} required className="sensoria-input" />
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Décrivez le lieu, l'ambiance, ce qui le rend spécial..." rows={3} maxLength={1000} className="sensoria-input resize-none" />
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Catégorie</label>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="sensoria-input appearance-none">
                {CATEGORIES.filter((c) => c.value).map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Ville</label>
              <input type="text" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="Ville" required className="sensoria-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Tarif</label>
              <select value={form.price_range} onChange={(e) => setForm((f) => ({ ...f, price_range: e.target.value }))} className="sensoria-input appearance-none">
                {PRICE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <input type="text" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Adresse complète (optionnel)" className="sensoria-input" />
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Département</label>
              <select value={form.department} onChange={(e) => {
                const dept = DEPARTMENTS.find((d) => d.code === e.target.value);
                setForm((f) => ({ ...f, department: e.target.value, region: dept?.region || f.region }));
              }} className="sensoria-input appearance-none">
                <option value="">Sélectionner...</option>
                {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Région</label>
              <select value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} className="sensoria-input appearance-none">
                <option value="">Auto (depuis département)</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <input type="text" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="Site web (optionnel)" className="sensoria-input" />
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Équipements & caractéristiques</label>
            <div className="flex flex-wrap gap-2">
              {FEATURE_OPTIONS.map((f) => (
                <button key={f} type="button" onClick={() => toggleFeature(f)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                    form.features.includes(f) ? "border-sensoria-accent/50 bg-sensoria-accent/10 text-sensoria-accent-light" : "border-sensoria-border text-sensoria-text-dim hover:border-sensoria-muted"
                  }`}>{f}</button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={submitting} className="sensoria-btn w-full">{submitting ? t("places_submitting") : t("places_submit")}</button>
        </form>
      )}

      {/* Search & Filters */}
      <form onSubmit={handleCitySearch} className="flex flex-wrap gap-2">
        <input type="text" value={filterCity} onChange={(e) => setFilterCity(e.target.value)} placeholder="Chercher par ville..." className="sensoria-input min-w-0 flex-1 basis-32" />
        <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} className="sensoria-input max-w-[180px] flex-shrink-0 appearance-none text-sm">
          <option value="">Toute la France</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" className="sensoria-btn flex-shrink-0 px-5 py-2 text-sm">Go</button>
      </form>
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.value} onClick={() => setFilterCat(c.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              filterCat === c.value ? "border-sensoria-accent/50 bg-sensoria-accent/10 text-sensoria-accent-light" : "border-sensoria-border text-sensoria-text-dim hover:border-sensoria-muted"
            }`}>{c.icon} {c.label}</button>
        ))}
      </div>

      {/* Places Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" /></div>
      ) : places.length === 0 ? (
        <div className="py-16 text-center"><div className="mb-3 text-4xl">📍</div><p className="text-sensoria-text-dim">{t("places_empty")}</p></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {places.map((place) => {
            const cat = CATEGORIES.find((c) => c.value === place.category) || CATEGORIES[0];
            return (
              <article key={place.id} className="sensoria-card min-w-0 overflow-hidden">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-sensoria-accent/10 text-xl">{cat.icon}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white truncate">{place.name}</h3>
                      {place.is_verified && <span className="text-[10px] text-sensoria-success">✓</span>}
                    </div>
                    <p className="truncate text-xs text-sensoria-text-dim">
                      📍 {place.city}{place.department ? ` (${place.department})` : ""}{place.address ? ` · ${place.address}` : ""}
                      {place.region && <span className="ml-1 text-sensoria-muted">· {place.region}</span>}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    {place.price_range && (
                      <span className="rounded-full bg-sensoria-surface px-2 py-0.5 text-xs text-sensoria-text-dim">{place.price_range}</span>
                    )}
                    {place.photo_count > 0 && (
                      <span className="text-[10px] text-sensoria-muted">📷 {place.photo_count}</span>
                    )}
                  </div>
                </div>
                {place.description && <p className="mt-2 text-sm leading-relaxed text-sensoria-text-dim line-clamp-2">{place.description}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <Stars rating={Math.round(place.avg_rating || 0)} />
                  <span className="text-xs text-sensoria-muted">{place.avg_rating ? Number(place.avg_rating).toFixed(1) : "–"} ({place.review_count || 0} avis)</span>
                </div>
                {(place.features || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {place.features.slice(0, 6).map((f) => (
                      <span key={f} className="rounded-full bg-sensoria-surface px-2 py-0.5 text-[10px] text-sensoria-text-dim">{f}</span>
                    ))}
                    {place.features.length > 6 && <span className="text-[10px] text-sensoria-muted">+{place.features.length - 6}</span>}
                  </div>
                )}
                {place.website && (
                  <a href={place.website.startsWith("http") ? place.website : `https://${place.website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-block text-xs text-sensoria-accent hover:text-sensoria-accent-light">🔗 Site web</a>
                )}

                {/* Review section */}
                <div className="mt-3 border-t border-sensoria-border/30 pt-3">
                  {reviewingPlace === place.id ? (
                    <div className="space-y-3 animate-fade-in">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[
                          { key: "rating", label: "Général" },
                          { key: "ambiance_rating", label: "Ambiance" },
                          { key: "respect_rating", label: "Respect" },
                          { key: "hygiene_rating", label: "Hygiène" },
                        ].map(({ key, label }) => (
                          <div key={key}>
                            <span className="text-[10px] text-sensoria-text-dim">{label}</span>
                            <Stars rating={review[key]} interactive onChange={(r) => setReview((rv) => ({ ...rv, [key]: r }))} />
                          </div>
                        ))}
                      </div>
                      <textarea value={review.comment} onChange={(e) => setReview((rv) => ({ ...rv, comment: e.target.value }))}
                        placeholder="Votre retour d'expérience..." rows={2} maxLength={500} className="sensoria-input resize-none text-sm" />
                      <div className="flex gap-2">
                        <button onClick={() => handleSubmitReview(place.id)} disabled={submitting} className="sensoria-btn px-4 py-1.5 text-sm">Publier</button>
                        <button onClick={() => { setReviewingPlace(null); setReview({ rating: 0, ambiance_rating: 0, respect_rating: 0, hygiene_rating: 0, comment: "" }); }}
                          className="px-3 py-1.5 text-sm text-sensoria-muted hover:text-white">Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setReviewingPlace(place.id)}
                      className="text-sm font-medium text-sensoria-accent hover:text-sensoria-accent-light transition-colors">
                      {t("places_review_cta")}
                    </button>
                  )}
                </div>
                {/* Claim button */}
                {!place.is_claimed && (
                  <div className="mt-2 border-t border-sensoria-border/20 pt-2">
                    <button
                      onClick={() => {
                        const name = window.prompt("Nom de votre établissement :");
                        const email = name ? window.prompt("Email de contact professionnel :") : null;
                        if (name && email) {
                          api(`/api/claims/${place.id}`, {
                            method: "POST",
                            body: JSON.stringify({ business_name: name, contact_email: email, proof_description: "Demande via annuaire" }),
                          }).then(() => {
                            setSuccess("Demande de revendication envoyée !");
                            setTimeout(() => setSuccess(""), 3000);
                          }).catch((err) => setError(err.message));
                        }
                      }}
                      className="text-xs text-sensoria-muted hover:text-sensoria-accent-light transition-colors"
                    >
                      🏢 C'est mon établissement
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
